<template>
  <div :class="[embeddedMode ? '' : 'mx-6 my-4']">
    <div class="md:flex md:items-center md:justify-between md:space-x-5">
      <div v-if="!embeddedMode" class="flex items-center space-x-5">
        <div class="flex-shrink-0">
          <div class="relative bg-emerald-600/70 rounded-xl p-1">
            <mdicon name="file-tree-outline" size="62" class="text-emerald-100" aria-hidden="true"></mdicon>
          </div>
        </div>
        <div class="">
          <h1 class="mb-1 text-2xl font-bold text-gray-900">{{ title }}</h1>
          <p class="text-sm font-medium text-gray-500">{{ subtitle }}</p>
        </div>
      </div>
      <div v-else class="flex items-center space-x-3 mb-1">
        <div class="flex-shrink-0">
          <div class="relative bg-violet-600/70 rounded-md p-1">
            <mdicon name="file-tree-outline" size="30" class="text-violet-100" aria-hidden="true"></mdicon>
          </div>
        </div>
        <div class="">
          <h1 class="mb-1 text-xl font-semibold text-gray-800">Bot Categories</h1>
        </div>
      </div>
      <div class="flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-3 sm:space-y-0 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
        <ProgressButton ref="analyzeProgressButton" title="Refresh" icon="refresh" @click="loadData"></ProgressButton>
        <button
          type="button"
          class="flex items-center gap-x-1 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          @click="onExport"
        >
          <mdicon name="download-outline" class="h-5 w-5 mr-1" aria-hidden="true"></mdicon>
          Export
        </button>
        <button
          v-tooltip.bottom="'Data Assistant'"
          @click="onToggleDA"
          :class="[
            'px-2 mr-2 rounded border border-transparent shadow-gradient-glow-green',
            dataAssistantStore.visible ? 'text-slate-100 bg-emerald-600/70 hover:bg-emerald-500/70' : 'text-slate-600 hover:bg-slate-100/70',
          ]"
        >
          <mdicon name="creation" size="22" aria-hidden="true"></mdicon>
        </button>
      </div>
    </div>

    <FiltersList class="mt-2" @select-filter="onSelectFilter" @show-filters="showFilters"></FiltersList>

    <!--
    <div class="w-full">
      <DbHorizon :data="testHorizonData" :series-height="30"></DbHorizon>
    </div>
    -->

    <div class="overflow-hidden mt-2 mb-2">
      <div class="flex items-center justify-between border-b border-gray-300 pb-1">
        <div class="flex items-center">
          <mdicon name="text" size="18" class="rounded-md bg-emerald-600/70 p-1 mr-2 text-white" aria-hidden="true"></mdicon>
          <h2 class="text-base font-semibold leading-7 text-gray-900">
            <span class="text-lg text-slate-800 font-semibold">{{ totalConversations }}</span> over
            <span class="text-md text-slate-700">{{ analyzedDateRange }} (UTC)</span>
          </h2>
        </div>
        <div class="flex items-center">
          <div class="flex rounded-md shadow-sm mr-2">
            <div class="relative flex-grow focus-within:z-10">
              <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                v-model="filters['global']"
                name="desktop-search-candidate"
                id="desktop-search-candidate"
                class="w-[200px] rounded-none rounded-l-md border-0 py-1.5 pl-10 text-sm leading-6 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
                placeholder="Filter"
              />
            </div>
            <button
              type="button"
              class="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              @click="clearFilter()"
            >
              <XMarkIcon class="-ml-0.5 h-5 w-5 text-gray-400" aria-hidden="true" />
            </button>
          </div>
          <Button v-tooltip.top="'Expand/Collapse all'" icon="pi pi-arrows-v" @click="toggleExpand" size="small" severity="secondary" class="mr-2" />
          <SelectButton
            v-tooltip.top="'Group time by ...'"
            v-model="selectedGroupingInterval"
            :options="groupingIntervalOptions"
            optionLabel="title"
            optionValue="value"
            :allowEmpty="false"
            aria-labelledby="basic"
            size="small"
            @change="onChangeGroupingInterval"
          />
        </div>
      </div>
    </div>

    <!-- TODO take sidebar width(left) and data analyzer width(right) dynamically in calc -->
    <div class="overflow-auto" :style="`width: calc(100vw - 20rem - ${dataAssistantStore.visibleWidth}px - 5px)`">
      <TreeTable
        v-model:filters="filters"
        v-model:expanded-keys="treeExpandedKeys"
        filterMode="strict"
        :value="categoriesTree"
        sortField="name"
        :sortOrder="1"
        removableSort
        scrollable
        scroll-direction="both"
        size="small"
        class="custom-scrollbar font-source"
        rowHover
      >
        <Column field="name" header="Name" expander frozen style="width: 400px" sortable class="flex items-center">
          <template #body="slotProps">
            <router-link :to="{ path: conversationsTargetRoute, query: { filter: getFilterCategories(slotProps.node) } }" class="px-2 py-1 flex items-center hover:underline">
              <div class="cursor-pointer font-medium">{{ slotProps?.node?.label }}</div>
              <mdicon name="open-in-new" size="20" class="ml-2 text-gray-300"></mdicon>
            </router-link>
          </template>
        </Column>

        <!--
        <Column field="count" header="Name" :expander="true" sortable class="" frozen style="width: 400px"></Column>
        -->
        <Column field="count" sortable frozen style="width: 80px">
          <template #header>
            <span class="text-sm text-center">Count</span>
          </template>
          <template #body="slotProps">
            <router-link :to="{ path: conversationsTargetRoute, query: { filter: getFilterCategories(slotProps.node) } }" class="flex items-center hover:underline w-full text-right">
              <div class="w-full cursor-pointer text-md font-medium">{{ slotProps?.node?.data?.count }}</div>
            </router-link>
          </template>
        </Column>
        <Column field="csat.avg" sortable style="width: 80px">
          <template #header>
            <span class="text-sm text-center">Avg CSat</span>
          </template>
          <template #body="slotProps">
            <div class="w-full text-right">{{ slotProps?.node?.data?.csat?.avg ? slotProps.node.data.csat.avg.toFixed(2) : '' }}</div>
          </template>
        </Column>
        <Column v-if="enableEscalated" field="escalated.doc_count" sortable style="width: 70px">
          <template #header>
            <span class="text-sm text-center" v-tooltip.top="'Escalated'">Esc</span>
          </template>
          <template #body="slotProps">
            <div class="w-full text-right">
              {{ slotProps?.node?.data?.escalated?.doc_count || 0 }}
            </div>
          </template>
        </Column>
        <Column v-if="enableEscalated" field="escalated.pct" sortable style="width: 70px">
          <template #header>
            <span class="text-sm text-center" v-tooltip.top="'Escalated %%'">Esc %%</span>
          </template>
          <template #body="slotProps">
            <div class="w-full text-right">{{ (slotProps?.node?.data?.escalated?.pct || 0).toFixed(0) }}%</div>
          </template>
        </Column>
        <Column v-if="enableTTA" field="tta.avg" sortable style="width: 80px">
          <template #header>
            <span class="text-sm text-center">Avg TTA</span>
          </template>
          <template #body="slotProps">
            <div class="w-full text-right">{{ getTTAText(slotProps?.node?.data?.tta?.avg) }}</div>
          </template>
        </Column>
        <Column v-for="(timeCol, idx) in timelineColumns" :key="timeCol.key" field="timeline" :style="`width: ${timeCol.width}px`">
          <template #header="slotProps">
            <span class="text-2xs text-center">{{ timeCol.header }}</span>
          </template>
          <template #body="slotProps">
            <div class="w-full text-sm px-1 py-1 rounded text-center" :style="getTimelineValueStyle(slotProps?.node?.data?.timeline[idx]?.doc_count || 0)">
              {{ slotProps?.node?.data?.timeline[idx]?.doc_count || 0 }}
            </div>
          </template>
        </Column>
        <!--
        <Column header="Volume over time" style="width: 50%">
          <template #body="slotProps">
            <div class="flex items-center w-full h-full">
              <DbSparkLine class="flex-auto h-6 w-full" :opactity="0.2" :data="slotProps?.node?.data?.timeline.map((x) => x.doc_count)"></DbSparkLine>
            </div>
          </template>
        </Column>
        -->
        <!--
        <template #footer>
          <div class="flex justify-content-start">
            <Button icon="pi pi-refresh" label="Reload" severity="warning" size="small" />
          </div>
        </template>
        -->
      </TreeTable>
    </div>

    <EditFiltersSidebar v-model:visible="filtersOpen"></EditFiltersSidebar>
  </div>
</template>

<script setup>
  import { onMounted, onBeforeUnmount, ref, reactive, computed, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useLayoutStore } from '@/store/LayoutStore';
  import { useBotConversationSearchStore } from '@/store/BotConversationSearchStore';
  import { useConversationSearchStore } from '@/store/ConversationSearchStore';
  import { useFiltersStore } from '@/store/FiltersStore';
  import { useDataAssistantStore } from '@/store/DataAssistantStore';
  import { useRbacStore } from '@/store/RBACStore';
  import { useSettingsStore } from '@/store/SettingsStore';
  import { stdBotConversationFilters, stdConversationFilters } from '@/analytics/filtersDefs';
  import { ArrowDownCircleIcon, HashtagIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/20/solid';
  import ProgressButton from '@/components/Buttons/ProgressButton.vue';
  import { useDateRangeStore } from '@/store/DaterangeStore';
  import DbUplot from '@/components/Charts/DbUplot.vue';
  import uPlot from 'uplot';
  import EditFiltersSidebar from '@/components/Filters/EditFiltersSidebar.vue';
  import FiltersList from '@/components/Filters/FiltersList.vue';
  import dbColors from '@/components/Charts/dbcolors';
  import { observable10Colors, tableauColors } from '@/components/Charts/dbcolors';
  import { formatSeconds } from '@/utils/formatSeconds';
  import TreeTable from 'primevue/treetable';
  import Column from 'primevue/column';
  import Button from 'primevue/button';
  import SelectButton from 'primevue/selectbutton';
  import DAConversation from '@/components/DataAssistant/DAConversation.vue';
  import { utils, writeFileXLSX } from 'xlsx';
  import DbSparkLine from '@/components/Charts/DbSparkLine.vue';
  import DbHorizon from '@/components/Charts/DbHorizon.vue';
  import dayjs from 'dayjs';
  import utc from 'dayjs/plugin/utc';
  import * as d3 from 'd3';
  import * as d3Color from 'd3-color';

  dayjs.extend(utc);

  const props = defineProps({
    title: {
      type: String,
      default: 'Categories',
    },
    subtitle: {
      type: String,
      default: 'How conversations were categorized',
    },
    conversationsTargetRoute: {
      type: String,
      default: '/bots/conversations',
    },
    enableTTA: {
      type: Boolean,
      default: true,
    },
    enableEscalated: {
      type: Boolean,
      default: true,
    },
    fields: {
      type: Object,
      default() {
        return {
          category: 'analysis.category',
          subcategory: 'analysis.subcategory',
          subsubcategory: 'analysis.subsubcategory',
          csat: 'CSat',
          tta: 'state.timeToAgent',
        };
      },
    },
    conversationsSource: {
      type: String,
      default: 'bot',
    },
  });

  const route = useRoute();
  const router = useRouter();
  const settingsStore = useSettingsStore();
  const layout = useLayoutStore();
  const conversationSearchStore = props.conversationsSource === 'bot' ? useBotConversationSearchStore() : useConversationSearchStore();
  const dateRangeStore = useDateRangeStore();
  const filtersStore = useFiltersStore();
  const rbac = useRbacStore();
  const dataAssistantStore = useDataAssistantStore();

  const loading = ref(false);

  const testHorizonData = generateTestHorizonData();

  // Tree of categories as returned by aggregation
  const categoriesTree = ref([]);
  // Timeline Columns in tree table
  const timelineColumns = ref([]);
  // All expanded nodes in a tree
  const treeExpandedKeys = ref({});
  // map key -> node for easier navigation
  let treeKeymap = {};

  const totalConversations = ref(0);
  const analyzedDateRange = ref('');
  const analyzedDateRangeFn = ref('');

  let timelineMax = 0;
  let timelineColorScale = d3.scaleSequential(d3.interpolateBlues).domain([0, 10]);

  // Filters
  const filters = ref({ global: '' });
  const onSetFilter = (value) => {
    filters.value = { global: value };
  };
  const clearFilter = () => {
    filters.value = { global: '' };
  };

  //const categoriesFiltersNames = ['analysis.category', 'analysis.subcategory', 'analysis.subsubcategory'];
  const categoriesFiltersNames = [props.fields.category, props.fields.subcategory, props.fields.subsubcategory];

  // Grouping intervals
  // todo computed, disable day if range > 3 month
  const groupingIntervalOptions = computed(() => {
    const opts = [
      { title: 'Week', value: '1w', endOf: 'week' },
      { title: 'Month', value: '1M', endOf: 'month' },
    ];
    if (dateRangeStore.durationDays <= 90) {
      opts.unshift({ title: 'Day', value: '1d', endOf: 'day' });
    }
    return opts;
  });
  const selectedGroupingInterval = ref('1d');
  const selectedGroupingIntervalData = computed(() => {
    return groupingIntervalOptions.value.find((x) => x.value === selectedGroupingInterval.value);
  });

  const reasonsList = ref([]);

  // Data assistant
  const daMessages = ref([]);

  const embeddedMode = ref(route.meta?.embeddedMode === 'true');
  const analyzeProgressButton = ref(null);
  const filtersOpen = ref(false);

  const dataTitle = 'Conversations Categories';

  const dataDescription =
    'Data contains a JSON dataset organized as a hierarchical tree that represents categorized Customer Service interactions over a specific period. The tree structure mirrors the three levels of categorization:\n' +
    '\n' +
    'Top-Level (Category): Each top-level node represents a major category (e.g., "Name": "WiFi/Network").\n' +
    'Second-Level (Subcategory): Each category node contains a "Subcategory" array. Each object in this array represents a subcategory under the top-level category.\n' +
    'Third-Level (Tertiary Category): Some subcategory nodes further contain a "TertiaryCategory" array. Each object in this array represents a tertiary categorization under the subcategory. (Not every subcategory will have tertiary nodes.)\n' +
    'Data in Each Node:\n' +
    '\n' +
    'Name: This key provides the name of the category, subcategory, or tertiary category.\n' +
    '\n' +
    'statistics: This object contains "rolled up" aggregated data for that node. It includes:\n' +
    '\n' +
    'Count: The total number of customer interactions for that node (i.e., for the entire category branch at that level).\n' +
    'AvgCSat: The average Customer Satisfaction score.\n' +
    'Escalated: The count of interactions escalated to a live agent.\n' +
    'PctEscalated: The percentage of interactions escalated relative to the total Count.\n' +
    'AvgTTA: The Average Time To Agent (i.e., the average time between escalation and an agent answering). This value can be 0 or null if not applicable.\n' +
    'timeline: An array of objects that shows time-series data for that node. Each object includes:\n' +
    '\n' +
    'date: A specific date (e.g., "10 Feb 2025").\n' +
    'count: The number of interactions on that date, aggregated for that node.\n' +
    'Each node\'s statistics and timeline are "rolled up," meaning that the metrics for a given node represent all interactions within that branch of the hierarchy.\n' +
    '\n' +
    'Usage:\n' +
    '\n' +
    'With this structured data, you can analyze trends and insights at various levels:\n' +
    '\n' +
    'Overall trends for a top-level category.\n' +
    'Detailed performance and trends for specific subcategories.\n' +
    'Granular insights for tertiary categories where applicable.\n\n' +
    `Today's date is ${dayjs().format('DD MMM YYYY')}. Note that data for today, this week or this month could be incomplete.`;

  const suggestedQuestions = ['What insights can you derive from this data?', 'What are the the most noticeable trends?'];

  watch(filtersStore.allFilters, (newValue) => {
    if (loading.value) {
      return;
    }
    console.log(`Filters changed: ${JSON.stringify(newValue)}`);
    loadData();
  });

  watch(
    () => dateRangeStore.timeIntervalChanged,
    (newValue) => {
      console.log(`Date range changed: ${JSON.stringify(newValue)}`);
      selectedGroupingInterval.value = dateRangeStore.groupingCalendarInterval;
      loadData();
    },
  );

  function onChangeGroupingInterval() {
    console.log(`Grouping interval changed: ${selectedGroupingInterval.value}`);
    loadData();
  }

  // todo reconsider - may want to enable to plot trend over time
  /*
  const uPlotCSatData = ref([]);
  const uPlotOptionsCSat = {
    scales: {
      x: { time: true },
      y: { range: { min: { soft: 0, mode: 2 }, max: { soft: 100, mode: 2 } } },
    },
    series: [
      {},
      {
        label: 'CSat',
        value: (u, v, sidx, didx) => (didx == null ? null : v == null ? '' : v.toFixed(1) + '%'),
        stroke: dbColors.hex2RGBA(observable10Colors[0], 0.7), //'rgba(78, 121, 167, 0.1 )',
        fill: dbColors.hex2RGBA(observable10Colors[0], 0.3), //'rgba(78, 121, 167, 0.4)',
      },
    ],
    axes: [
      {},
      {
        values: (u, vals, space) => vals.map((v) => +v.toFixed(1) + '%'),
      },
    ],
  };*/

  async function loadData() {
    analyzeProgressButton.value.startProgress();
    loading.value = true;

    // Option: Align time frame on beginning/end of day / week / month
    // todo revisit, for now - maintain original from/to
    const originalFrom = dateRangeStore.fromTs;
    const originalTo = dateRangeStore.toTs;
    const rangeFrom = originalFrom; //dayjs(originalFrom).utc().startOf(selectedGroupingIntervalData.value.endOf).valueOf();
    const rangeTo = originalTo; //dayjs(originalTo).utc().endOf(selectedGroupingIntervalData.value.endOf).valueOf();
    const calendarInterval = selectedGroupingInterval.value;

    const statsGroupings = [
      {
        name: 'timeline',
        title: 'Date',
        type: 'date_histogram',
        calendar_interval: calendarInterval,
        extended_bounds: { min: rangeFrom, max: rangeTo },
      },
      {
        name: 'csat',
        title: 'CSat',
        type: 'stats',
        field: props.fields.csat, // 'CSat',
      },
    ];

    const groupings = [
      // todo reconsider - may want to enable to plot trend over time
      /*
      {
        name: 'timeline',
        title: 'Date',
        type: 'date_histogram',
        interval: dateRangeStore.timeInterval.interval,
        extended_bounds: { min: dateRangeStore.timeInterval.from, max: dateRangeStore.timeInterval.to },
        groupings: [
          { name: 'escalated', type: 'filter', query: { term: { 'state.escalated': true } }, groupings: [{ name: 'reasons', title: 'Reasons', type: 'terms', field: 'state.handoffReason', size: 3 }] },
        ],
      },
      */
      {
        name: 'category',
        title: 'Category',
        type: 'terms',
        field: props.fields.category, //'analysis.category',
        missing: 'N/A',
        size: 100,
        groupings: [
          {
            name: 'subcategory',
            title: 'Subcategory',
            type: 'terms',
            field: props.fields.subcategory, //'analysis.subcategory',
            missing: 'N/A',
            size: 100,
            groupings: [
              {
                name: 'subsubcategory',
                title: 'Tertiary',
                type: 'terms',
                field: props.fields.subsubcategory, // 'analysis.subsubcategory',
                missing: 'N/A',
                size: 100,
                groupings: statsGroupings,
              },
            ],
          },
        ],
      },
      //{ name: 'escalated', type: 'filter', query: { term: { 'state.escalated': true } }, groupings: [] },
    ];

    // Optionally enable TTA, escalated
    if (props.enableTTA && 'tta' in props.fields) {
      statsGroupings.push({ name: 'withTTA', type: 'filter', query: { range: { 'stats.timeToAgent': { gt: 0, lte: 7200 } } }, groupings: [{ name: 'tta', type: 'stats', field: 'stats.timeToAgent' }] });
    }

    if (props.enableEscalated) {
      statsGroupings.push({ name: 'escalated', type: 'filter', query: { term: { 'state.escalated': true } } });
    }

    const aggregateResult = await conversationSearchStore.aggregate(groupings);
    processAggregateResult(aggregateResult);
    loading.value = false;
    analyzeProgressButton.value.stopProgress();
  }

  function convertAggregationsToTreeNodes(aggregations) {
    function processAggregation(aggregation, parentKey, parentPath, level) {
      const nodes = [];
      let rolledUpData = { timeline: [], csat: null, tta: null, escalated: null };

      if (!aggregation || !aggregation.buckets) return { nodes, data: rolledUpData };

      aggregation.buckets.forEach((bucket) => {
        //const { node, data } = processBucket(bucket, parentKey, parentPath, level, bucket.key === 'N/A' && level > 0);
        const { node, data } = processBucket(bucket, parentKey, parentPath, level);
        if (node) {
          nodes.push(node);
        }
        if (data) {
          // Merge data into rolledUpData
          if (data.timeline) {
            rolledUpData.timeline = mergeTimelines(rolledUpData.timeline, data.timeline);
          }
          if (data.csat) {
            rolledUpData.csat = combineStats(rolledUpData.csat, data.csat);
          }
          if (data.tta) {
            rolledUpData.tta = combineStats(rolledUpData.tta, data.tta);
          }
          if (data.escalated) {
            rolledUpData.escalated = mergeDocCount(rolledUpData.escalated, data.escalated);
          }
        }
      });

      return { nodes, data: rolledUpData };
    }

    function processBucket(bucket, parentKey, parentPath, level) {
      const key = parentKey ? `${parentKey}-${bucket.key}` : bucket.key;
      const path = parentPath.concat([bucket.key]);

      let nodeData = { count: bucket.doc_count };

      let node = {
        key: key,
        path: path,
        label: bucket.key,
        level: level,
        data: nodeData,
      };

      // Initialize timeline and csat data if they exist
      if (bucket.timeline && bucket.timeline.buckets) {
        nodeData.timeline = bucket.timeline.buckets.map((b) => ({ ...b }));
      }

      if (bucket.csat) {
        nodeData.csat = {
          count: bucket.csat.count,
          min: bucket.csat.min,
          max: bucket.csat.max,
          sum: bucket.csat.sum,
          avg: bucket.csat.avg,
        };
      }

      if (bucket?.withTTA?.tta) {
        nodeData.tta = {
          count: bucket.withTTA.tta.count,
          min: bucket.withTTA.tta.min,
          max: bucket.withTTA.tta.max,
          sum: bucket.withTTA.tta.sum,
          avg: bucket.withTTA.tta.avg,
        };
      }

      if (bucket.escalated) {
        nodeData.escalated = { doc_count: bucket.escalated.doc_count };
      }

      // Process any nested aggregations
      let childResult = { nodes: [], data: { timeline: [], csat: null } };
      for (const prop in bucket) {
        if (prop !== 'key' && prop !== 'doc_count' && prop !== 'timeline' && prop !== 'csat' && prop !== 'tta' && bucket[prop] && bucket[prop].buckets) {
          const result = processAggregation(bucket[prop], key, path, level + 1);
          if (result.nodes.length > 0) {
            childResult.nodes.push(...result.nodes);
          }
          if (result.data) {
            // Merge data
            if (result.data.timeline) {
              childResult.data.timeline = mergeTimelines(childResult.data.timeline, result.data.timeline);
            }
            if (result.data.csat) {
              childResult.data.csat = combineStats(childResult.data.csat, result.data.csat);
            }
            if (result.data.tta) {
              childResult.data.tta = combineStats(childResult.data.tta, result.data.tta);
            }
            if (result.data.escalated) {
              childResult.data.escalated = mergeDocCount(childResult.data.escalated, result.data.escalated);
            }
          }
        }
      }

      // Roll up data from children
      if (childResult.nodes.length > 0 || childResult.data.timeline.length > 0 || childResult.data.csat || childResult.data.tta) {
        if (childResult.nodes.length > 0) node.children = childResult.nodes;

        if (!nodeData.timeline) nodeData.timeline = [];
        if (!nodeData.csat) nodeData.csat = null;

        // Merge timelines from child nodes
        if (childResult.data.timeline.length > 0) {
          nodeData.timeline = mergeTimelines(nodeData.timeline, childResult.data.timeline);
        }
        // Merge csat from child nodes
        if (childResult.data.csat) {
          nodeData.csat = combineStats(nodeData.csat, childResult.data.csat);
        }
        if (childResult.data.tta) {
          nodeData.tta = combineStats(nodeData.tta, childResult.data.tta);
        }
        if (childResult.data.escalated) {
          nodeData.escalated = mergeDocCount(nodeData.escalated, childResult.data.escalated);
          nodeData.escalated.pct = (nodeData.escalated.doc_count / nodeData.count) * 100;
        }
      }

      // Return node and data
      return { node, data: nodeData };
    }

    // Function to merge  by summing doc_counts
    function mergeDocCount(prop1, prop2) {
      return {
        doc_count: (prop1?.doc_count || 0) + (prop2?.doc_count || 0),
      };
    }
    // Function to merge timeline buckets by summing doc_counts for matching keys
    function mergeTimelines(timeline1, timeline2) {
      const merged = {};

      // Add entries from the first timeline
      timeline1.forEach((bucket) => {
        merged[bucket.key] = { ...bucket };
      });

      // Add or merge entries from the second timeline
      timeline2.forEach((bucket) => {
        if (merged[bucket.key]) {
          merged[bucket.key].doc_count += bucket.doc_count;
        } else {
          merged[bucket.key] = { ...bucket };
        }

        // Temp
        //merged[bucket.key].doc_count = merged[bucket.key].doc_count > 0 ? merged[bucket.key].doc_count + 10 : 0; //Math.floor(Math.random() * 100);
        //merged[bucket.key].doc_count = Math.floor(Math.random() * 1000);

        if (!('keyDate' in merged[bucket.key])) {
          // TODO Take into account selected timezone here, when implemented in UI. For now - UTC.
          switch (selectedGroupingInterval.value) {
            case '1d':
              merged[bucket.key].keyDate = dayjs(bucket.key).utc().format('DD MMM');
              merged[bucket.key].keyDateEx = dayjs(bucket.key).utc().format('DD MMM YYYY');
              break;
            case '1w':
              merged[bucket.key].keyDate = 'W ' + dayjs(bucket.key).utc().format('DD MMM');
              merged[bucket.key].keyDateEx = 'Week of ' + dayjs(bucket.key).utc().format('DD MMM YYYY');
              break;
            case '1M':
              merged[bucket.key].keyDate = dayjs(bucket.key).utc().format('MMM YYYY');
              merged[bucket.key].keyDateEx = dayjs(bucket.key).utc().format('MMM YYYY');
              break;
          }
        }

        if (merged[bucket.key].doc_count > timelineMax) {
          timelineMax = merged[bucket.key].doc_count;
        }
      });

      // Convert the merged object back to an array and sort by key
      return Object.values(merged).sort((a, b) => a.key - b.key);
    }

    // Function to combine stat aggregations
    function combineStats(stats1, stats2) {
      if (!stats1 || stats1.count === 0) return stats2;
      if (!stats2 || stats2.count === 0) return stats1;

      const combinedCount = stats1.count + stats2.count;
      const combinedSum = stats1.sum + stats2.sum;

      return {
        count: combinedCount,
        min: stats1.min !== null && stats2.min !== null ? Math.min(stats1.min, stats2.min) : stats1.min || stats2.min,
        max: stats1.max !== null && stats2.max !== null ? Math.max(stats1.max, stats2.max) : stats1.max || stats2.max,
        sum: combinedSum,
        avg: combinedCount > 0 ? combinedSum / combinedCount : null,
      };
    }

    // Start processing from the top-level aggregation
    const topLevelAggregationName = Object.keys(aggregations)[0];
    const topLevelAggregation = aggregations[topLevelAggregationName];

    const { nodes } = processAggregation(topLevelAggregation, '', [], 0);
    return nodes;
  }

  // Filter out "N/A" nodes with only one child
  function filterCategoriesTree(nodes) {
    // Map over nodes to process each one
    return nodes.map((node) => {
      // If the node has children, filter them recursively first
      if (node.children && Array.isArray(node.children)) {
        node.children = filterCategoriesTree(node.children);

        // Check if there's exactly one child and its label is "N/A"
        if (node.children.length === 1 && node.children[0].label === 'N/A') {
          // Remove the "N/A" child by setting children to an empty array
          node.children = [];
        }
      }
      return node;
    });
  }

  // Post-process the categories tree data:
  // - Extract total and date range
  // - set up dates columns
  function postProcessCategoriesTree(categoriesTreeData) {
    totalConversations.value = 0;
    analyzedDateRange.value = '';
    analyzedDateRangeFn.value = '';

    if (!Array.isArray(categoriesTreeData) || categoriesTreeData.length <= 0) {
      timelineColumns.value = [];
      return;
    }

    if (!Array.isArray(categoriesTreeData[0]?.data?.timeline) || categoriesTreeData[0].data.timeline.length <= 0) {
      timelineColumns.value = [];
      return;
    }

    const columns = categoriesTreeData[0].data.timeline.map((t) => {
      return { key: t.key, header: t.keyDate, headerEx: t.keyDateEx, max: 0, width: 34 };
    });

    if (!Array.isArray(columns) || columns.length <= 0) {
      timelineColumns.value = [];
      return;
    }

    const ts = Date.now();
    const startDate = columns[0].key;
    const endDate = columns[columns.length - 1].key;
    let endDateEndOfInterval = dayjs(endDate).utc().endOf(selectedGroupingIntervalData.value.endOf);
    if (endDateEndOfInterval > ts) {
      endDateEndOfInterval = ts;
    }

    analyzedDateRange.value = `${dayjs(startDate).utc().format('YYYY-MM-DD')} - ${dayjs(endDateEndOfInterval).utc().format('YYYY-MM-DD')}`;
    analyzedDateRangeFn.value = `${dayjs(startDate).utc().format('YYYY-MM-DD')}_${dayjs(endDateEndOfInterval).utc().format('YYYY-MM-DD')}`;

    let total = 0;

    categoriesTreeData.forEach((node) => {
      total += node.data.count;
      if (Array.isArray(node?.data?.timeline)) {
        node.data.timeline.forEach((t, idx) => {
          if (t.doc_count > columns[idx].max) {
            columns[idx].max = t.doc_count;
            if (columns[idx].max >= 1000) {
              columns[idx].width = 70;
            } else if (columns[idx].max >= 100) {
              columns[idx].width = 56;
            } else if (columns[idx].max >= 10) {
              columns[idx].width = 46;
            }
          }
        });
      }
    });

    timelineColumns.value = columns;
    timelineColorScale = d3.scaleSequential(d3.interpolateBlues).domain([0, timelineMax]);

    totalConversations.value = total;
  }

  function processAggregateResult(aggregateResult) {
    timelineMax = 0;
    timelineColorScale = d3.scaleSequential(d3.interpolateBlues).domain([0, 10]);

    if (!aggregateResult.success) {
      reasonsList.value = [];
      console.log(`Error aggregating data: ${aggregateResult.error}`);
      return;
    }

    let categoriesData = aggregateResult?.result?.aggregations || null;

    let categoriesTreeData = convertAggregationsToTreeNodes(categoriesData, 'category');

    // Remove redundant "N/A" nodes
    categoriesTreeData = filterCategoriesTree(categoriesTreeData);

    postProcessCategoriesTree(categoriesTreeData);

    if (Array.isArray(categoriesTreeData) && categoriesTreeData.length > 0) {
      categoriesTree.value = categoriesTreeData;
    } else {
      categoriesTree.value = [];
    }

    // Prepare data for Data Assistant
    const daData = treeNodesToDAJson(categoriesTree.value);
    dataAssistantStore.setData(daData);
  }

  function handleDbEvent(event) {
    switch (event.type) {
      case 'zoom': {
        console.log(`Handle Zoom`, event);
        let from = event?.minDate || null;
        let to = event?.maxDate || null;
        if (from && to) {
          dateRangeStore.setDateRange(from, to);
          loadData();
        }
        break;
      }
    }
  }

  function onSelectFilter(filter) {
    // TODO pass which filter to make active
    showFilters();
  }

  function showFilters() {
    filtersOpen.value = true;
  }

  function getFilterQueryConversations(data) {
    return [encodeURIComponent(`state.handoffReason:${data.reason}`)];
  }

  function getFilterCategories(node) {
    const categoriesFilters = (node.path || [])
      .map((p, idx) => {
        return idx < 3 ? encodeURIComponent(`${categoriesFiltersNames[idx]}:${p !== 'N/A' ? p : null}`) : null;
      })
      .filter((x) => x);
    return categoriesFilters;
  }

  // Convert tree data to JSON for Data Assistant analysis
  // this format should be more clear for LLM to analyze
  function treeNodesToDAJson(nodes) {
    return nodes.map((n) => {
      const jn = {
        Name: '',
        statistics: {
          Count: 0,
          AvgCSat: 0,
          Escalated: 0,
          PctEscalated: 0,
          AvgTTA: 0,
        },
        timeline: [],
      };
      jn.Name = n.label;
      jn.statistics.Count = n.data.count;
      jn.statistics.AvgCSat = parseFloat((n.data.csat?.avg || 0).toFixed(2));
      jn.statistics.AvgTTA = parseFloat((n.data.tta?.avg || 0).toFixed(2));
      jn.statistics.Escalated = n.data?.escalated?.doc_count;
      jn.statistics.PctEscalated = parseInt((n.data?.escalated?.pct || 0).toFixed(0));

      timelineColumns.value.forEach((tc, idx) => {
        //jn[tc.header] = n.data.timeline[idx]?.doc_count || 0;
        jn.timeline.push({
          date: tc.headerEx,
          count: n.data.timeline[idx]?.doc_count || 0,
        });
      });

      if (Array.isArray(n.children) && n.children.length > 0) {
        const childPropName = n.level === 0 ? 'Subcategory' : 'TertiaryCategory';
        jn[childPropName] = treeNodesToDAJson(n.children);
      }

      return jn;
    });
  }

  function treeNodesToJson(nodes, jsonNodes) {
    nodes.forEach((n) => {
      const jn = {
        Level: n.level,
        Category: '',
        Subcategory: '',
        Tertiary: '',
        Count: 0,
        AvgCSat: 0,
        Escalated: 0,
        'Escalated%': 0,
        AvgTTA: 0,
      };
      if (n.path.length >= 1) {
        jn.Category = n.path[0];
      }
      if (n.path.length >= 2) {
        jn.Subcategory = n.path[1];
      }
      if (n.path.length >= 3) {
        jn.Tertiary = n.path[2];
      }
      jn.Count = n.data.count;
      jn.AvgCSat = n.data.csat?.avg;
      jn.AvgTTA = n.data.tta?.avg;
      jn.Escalated = n.data?.escalated?.doc_count;
      jn['Escalated%'] = parseInt((n.data?.escalated?.pct || 0).toFixed(0));

      timelineColumns.value.forEach((tc, idx) => {
        jn[tc.header] = n.data.timeline[idx]?.doc_count || 0;
      });

      jsonNodes.push(jn);
      if (Array.isArray(n.children)) {
        treeNodesToJson(n.children, jsonNodes);
      }
    });
  }

  function onExport() {
    const categoriesListData = [];
    treeNodesToJson(categoriesTree.value, categoriesListData);

    const wsCategories = utils.json_to_sheet(categoriesListData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, wsCategories, 'Categories');
    writeFileXLSX(wb, `VOC_CategoriesReport_${settingsStore.tenantName}_${analyzedDateRangeFn.value}.xlsx`);
  }

  function onToggleDA() {
    if (dataAssistantStore.visible) {
      dataAssistantStore.hide();
    } else {
      dataAssistantStore.show();
    }
  }

  function toggleExpand() {
    const expandedKeys = {};

    function expandAll(nodes) {
      nodes.forEach((n) => {
        if (Array.isArray(n.children)) {
          expandedKeys[n.key] = true;
          expandAll(n.children);
        }
      });
    }
    if (Object.keys(treeExpandedKeys.value).length === 0) {
      expandAll(categoriesTree.value);
      treeExpandedKeys.value = expandedKeys;
    } else {
      treeExpandedKeys.value = {};
    }
  }

  function getTTAText(val) {
    const tta = val || null;
    if (!tta || isNaN(tta)) {
      return '-';
    }
    return formatSeconds(tta);
  }

  function getNodeTitleClass(node) {
    if (node?.level === 0) {
      return 'font-semibold';
    } else if (node?.level === 1) {
      return 'font-medium';
    } else {
      return 'font-normal';
    }
  }

  function rgb2rgba(rgb, opacity) {
    let c = d3Color.color(rgb);
    c.opacity = opacity;
    return c + '';
  }

  function getTimelineValueStyle(val) {
    if (val > 0) {
      const c = timelineColorScale(val);
      const co = rgb2rgba(c, 0.3);
      return `background-color: ${co};color:#334155;font-weight:600;`;
    } else {
      return 'color:#cbd5e1;';
    }
  }

  function generateTestHorizonData() {
    let chartData = [];
    let valuesS1 = [];
    let valuesS2 = [];
    let sTS = Date.now() - 100 * 3600 * 1000;

    for (let i = 0; i < 100; i++) {
      let cTs = sTS + i * 3600 * 1000;
      let d = new Date(cTs);
      valuesS1.push({ date: d, value: Math.random() });
      valuesS2.push({ date: d, value: Math.random() });
    }
    chartData.push({ key: 'Series 1', values: valuesS1 });
    chartData.push({ key: 'Series 2', values: valuesS2 });
    return chartData;
  }

  onMounted(() => {
    if (props.conversationsSource === 'bot') {
      filtersStore.initFilters('botconv', stdBotConversationFilters);
    } else {
      filtersStore.initFilters('conv', stdConversationFilters);
    }
    dataAssistantStore.init({
      assistantDataTitle: dataTitle,
      assistantDataDescription: dataDescription,
      assistantSuggestedQuestions: suggestedQuestions,
      show: false, //rbac.canUseDataAssistant,
    });
    selectedGroupingInterval.value = dateRangeStore.groupingCalendarInterval;
    loadData();
  });

  onBeforeUnmount(() => {
    // todo
    dataAssistantStore.hide();
  });
</script>
<style>
  /*
  .p-frozen-column {
    background-color: red;
    flex: 0 0 auto;
    width: 400px;
  }
  .p-treetable-scrollable .p-treetable-thead > tr > th,
  .p-treetable-scrollable .p-treetable-tbody > tr > td,
  .p-treetable-scrollable .p-treetable-tfoot > tr > td {
    display: flex;
    flex: 1 1 0;
    align-items: center;
  }
  */

  /* custom scrollbar */
  .p-treetable-wrapper::-webkit-scrollbar {
    height: 18px;
  }

  .p-treetable-wrapper::-webkit-scrollbar-track {
    background-color: transparent;
  }

  .p-treetable-wrapper::-webkit-scrollbar-thumb {
    background-color: #d6dee1;
    border-radius: 18px;
    border: 6px solid transparent;
    background-clip: content-box;
  }

  .p-treetable-wrapper::-webkit-scrollbar-thumb:hover {
    background-color: #e2e8f0;
  }

  .p-treetable-wrapper::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
  }

  .p-treetable-wrapper::-webkit-scrollbar-thumb:hover {
    background-color: #64748b;
  }

  .p-selectbutton .p-button {
    font-size: 0.8rem;
  }
</style>
